/* =============================================================================
   Thirsty Threads - Related Products fallback loader
   Added 9 September 2026.

   WHY THIS EXISTS
   Ella renders the "Related Products" block as 10 skeleton placeholder cards
   (each reading "Example product title") and relies on
   theme.js -> loaderRecommendationsBlock() -> halo.buildRecommendationBlock()
   to fetch the real products and swap them in.

   On our product templates that chain never runs, so the placeholder cards stay
   on screen permanently on every PDP. The /recommendations/products endpoint
   itself is healthy - it returns real product markup - so only the trigger is
   broken. This file performs that fetch and swap independently.

   SAFE BY DESIGN
   - Does nothing unless the block is still in its loading state, so if Ella's
     own loader is ever fixed or fires first, this quietly stands down.
   - Waits briefly before arming, to give Ella's loader first go.
   - Loads only when the block is near the viewport, so it costs nothing for the
     many visitors who never scroll that far (matters for paid mobile traffic).
   - Uses three independent triggers - IntersectionObserver, a scroll/resize
     proximity check, and a final timed backstop - because the block must never
     be left showing placeholder cards.
   - Removes the block entirely rather than leaving placeholders if the fetch
     fails or returns nothing.
   ============================================================================= */
(function () {
  var ARM_DELAY = 1000;      /* let Ella's own loader go first */
  var NEAR_VIEWPORT = 600;   /* px below the fold that counts as "near" */
  var BACKSTOP_DELAY = 8000; /* last resort if nothing else has fired */

  var done = false;

  function swapIn(block) {
    if (done) return;
    done = true;
    block.dataset.ttRecLoaded = '1';

    fetch(block.dataset.url)
      .then(function (response) {
        if (!response.ok) throw new Error('Bad response');
        return response.text();
      })
      .then(function (text) {
        var parsed = document.createElement('div');
        parsed.innerHTML = text;

        var fresh = parsed.querySelector('[data-recommendations-block]');

        if (!fresh || !fresh.innerHTML.trim().length) {
          block.remove();
          return;
        }

        block.innerHTML = fresh.innerHTML;

        /* Guard against swapping in another set of placeholders. */
        if (
          !block.querySelector('.product-item') ||
          block.querySelector('.product-item--loadingNoInfo')
        ) {
          block.remove();
          return;
        }

        /* Re-initialise Ella's slider if this block is laid out as a carousel.
           theme.js exposes productBlockSilder on window.sharedFunctions. */
        if (
          block.dataset.layout === 'slider' &&
          window.jQuery &&
          window.sharedFunctions &&
          typeof window.sharedFunctions.productBlockSilder === 'function'
        ) {
          try {
            window.sharedFunctions.productBlockSilder(window.jQuery(block));
          } catch (e) {
            /* Slider init is cosmetic - the products are already on the page. */
          }
        }
      })
      .catch(function () {
        block.remove();
      });
  }

  function stillWaiting(block) {
    var container = block.querySelector('.wrapper-container');
    return !!container &&
      container.classList.contains('product-recommendations-loading');
  }

  function isNear(block) {
    var top = block.getBoundingClientRect().top;
    return top < window.innerHeight + NEAR_VIEWPORT;
  }

  function arm() {
    var block = document.querySelector('[data-recommendations-block]');
    if (!block || !block.dataset.url) return;

    /* Ella's loader already handled it - nothing to do. */
    if (!stillWaiting(block)) return;

    function go() {
      if (done) return;
      cleanup();
      swapIn(block);
    }

    function onScroll() {
      if (isNear(block)) go();
    }

    var observer = null;

    function cleanup() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (observer) observer.disconnect();
      clearTimeout(backstop);
    }

    /* Trigger 1 - IntersectionObserver. */
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) go();
        },
        { rootMargin: NEAR_VIEWPORT + 'px 0px' }
      );
      observer.observe(block);
    }

    /* Trigger 2 - proximity on scroll or resize, and an immediate check in case
       the block is already near the viewport on load (short pages, deep links). */
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();

    /* Trigger 3 - timed backstop, so a placeholder is never left on screen. */
    var backstop = setTimeout(go, BACKSTOP_DELAY);
  }

  function init() {
    setTimeout(arm, ARM_DELAY);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();